import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, FileText, Download } from 'lucide-react';
import { exportAnalysisToPDF } from '../../utils/ProfessionalPDFExport';


const LogicFlow = ({ code }) => {
    const rawLines = code.replace(/\\n/g, '\n').split('\n').map(l => l.trim()).filter(l => l);

    // Simple heuristic parser for Python-like logic
    const steps = [];
    let currentStep = null;

    rawLines.forEach(line => {
        // Docstring handling - skip empty or purely structural quotes
        if ((line.startsWith("'") || line.startsWith('"')) && line.replace(/['"]/g, '').trim().length > 0) {
            steps.push({ type: 'header', content: line.replace(/['"]/g, '') });
            return;
        }

        // Skip structural quotes alone
        if (line === '"""' || line === "'''" || line === '""' || line === "''") return;

        // Decision Tiers (if/elif/else)
        if (line.startsWith('if ') || line.startsWith('elif ')) {
            const match = line.match(/(?:if|elif)\s+(.+):/);
            if (match) {
                if (!currentStep || currentStep.type !== 'decision') {
                    currentStep = { type: 'decision', title: 'Conditional Tiering', rules: [] };
                    steps.push(currentStep);
                }
                currentStep.rules.push({ condition: match[1], action: '' });
            }
        } else if (line.startsWith('else:')) {
            if (currentStep && currentStep.type === 'decision') {
                currentStep.rules.push({ condition: 'All other cases', action: '' });
            }
        } else if (line.includes(' = ') && !line.startsWith('return')) {
            const [variable, value] = line.split('=').map(s => s.trim());
            if (currentStep && currentStep.type === 'decision' && currentStep.rules.length > 0) {
                const prev = currentStep.rules[currentStep.rules.length - 1].action;
                const next = `${variable.replace(/_/g, ' ')} set to ${value}`;
                currentStep.rules[currentStep.rules.length - 1].action = prev ? `${prev}\n${next}` : next;
            } else {
                steps.push({ type: 'calculation', variable, value, title: 'Calculated Parameter' });
                currentStep = null;
            }
        } else {
            // Handle standalone expressions (like 'S = ...' or math formulas)
            const cleanLine = line.replace('return ', '').trim();
            if (currentStep && currentStep.type === 'decision' && currentStep.rules.length > 0) {
                const prev = currentStep.rules[currentStep.rules.length - 1].action;
                currentStep.rules[currentStep.rules.length - 1].action = prev ? `${prev}\n${cleanLine}` : cleanLine;
            } else {
                steps.push({ type: 'calculation', variable: 'Result', value: cleanLine, title: 'Mathematical Formula' });
                currentStep = null;
            }
        }
    });

    return (
        <div className="space-y-6">
            {steps.map((step, idx) => (
                <div key={idx} className="relative">
                    {step.type === 'header' && (
                        <div className="bg-slate-50 border-l-4 border-indigo-500 p-4 rounded-r-xl mb-4">
                            <p className="text-xs font-bold text-slate-600 italic leading-relaxed">
                                "{step.content.trim()}"
                            </p>
                        </div>
                    )}

                    {step.type === 'decision' && (
                        <div className="bg-white border border-slate-200 rounded-[2rem] overflow-hidden shadow-sm">
                            <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 flex items-center gap-2">
                                <div className="w-1.5 h-1.5 rounded-full bg-indigo-500" />
                                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{step.title}</span>
                            </div>
                            <div className="divide-y divide-slate-100">
                                {step.rules.map((rule, ridx) => (
                                    <div key={ridx} className="px-6 py-4 flex flex-col md:flex-row md:items-center justify-between gap-4 group hover:bg-slate-50/50 transition-colors">
                                        <div className="flex flex-col gap-1">
                                            <span className="text-[8px] font-black text-indigo-400 uppercase tracking-tighter">Condition</span>
                                            <span className="text-xs font-black text-slate-700 font-mono bg-slate-100 px-2 py-0.5 rounded-md">
                                                {rule.condition.replace(/_/g, ' ')}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <div className="w-4 h-[1px] bg-slate-200" />
                                            <div className="flex flex-col items-end">
                                                <span className="text-[8px] font-black text-emerald-500 uppercase tracking-tighter">Resulting Action</span>
                                                <span className="text-xs font-black text-slate-900 uppercase tracking-tight text-right" style={{ whiteSpace: 'pre-line' }}>
                                                    {rule.action || "Continue analysis"}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {step.type === 'calculation' && (
                        <div className="bg-indigo-50/50 border border-indigo-100 rounded-3xl p-6 flex items-center justify-between group transition-all hover:bg-indigo-50">
                            <div className="flex flex-col gap-1">
                                <span className="text-[9px] font-black text-indigo-500 uppercase tracking-widest">{step.title}</span>
                                <span className="text-xs font-black text-slate-900 uppercase">{step.variable.replace(/_/g, ' ')}</span>
                            </div>
                            <div className="flex items-center gap-4">
                                <div className="text-right">
                                    <span className="text-[8px] font-black text-slate-400 uppercase tracking-tighter block mb-1">Execution Rule</span>
                                    <span className="text-sm font-black text-indigo-600 font-mono tracking-tighter bg-white px-3 py-1 rounded-xl shadow-sm border border-indigo-100/50">
                                        {step.value}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};


const FormulaReportModal = ({ isOpen, onClose, city }) => {

    if (!city) return null;

    const handleDownload = () => {
        exportAnalysisToPDF(city);
    };

    const SectionHeader = ({ number, title }) => (
        <div className="flex items-center gap-3 mb-4 group/header">
            <span className="text-xl font-black text-slate-200 group-hover/header:text-indigo-400 transition-colors tabular-nums">
                {number.toString().padStart(2, '0')}
            </span>
            <div className="h-[1px] flex-1 bg-slate-100"></div>
            <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{title}</span>
        </div>
    );

    const FormulaBlock = ({ profile }) => {
        return (
            <div className="mb-20 relative pl-8 border-l-[3px] border-slate-100">
                <div className="space-y-8">

                    {/* Custom Los Gatos R-1 FAR Highlight Alert */}
                    {isLosGatos && profile.key === 'mfa' && (
                        <div className="p-5 bg-blue-50/80 border border-blue-200/80 rounded-2xl text-blue-900 text-xs font-semibold leading-relaxed shadow-sm">
                            <div className="flex items-center gap-2 mb-2">
                                <div className="w-1.5 h-1.5 rounded-full bg-blue-600 animate-pulse" />
                                <strong className="text-[11px] uppercase tracking-wider text-blue-800">Los Gatos R-1 FAR Step-Down Formula</strong>
                            </div>
                            <p className="text-slate-600 mb-3">
                                The combined FAR consists of a Residential FAR and a Garage FAR, step-down calculated based on net lot area:
                            </p>
                            <code className="block px-4 py-3 bg-white border border-blue-100 rounded-xl text-blue-900 font-mono text-[11px] leading-relaxed shadow-inner">
                                <span className="text-slate-400"># Standard Lots (A &gt;= 5.0):</span><br />
                                Residential FAR = 0.35 - (A - 5) * 0.20 / 25<br />
                                Garage FAR      = 0.10 - (A - 5) * 0.07 / 25<br /><br />
                                <span className="text-slate-400"># Small Lots (A &lt; 5.0):</span><br />
                                Small Lot FAR   = 0.49 - (A - 1) * 0.66 / A &nbsp;(min 0.35)<br />
                                Garage FAR      = 0.10<br /><br />
                                Total FAR = Residential FAR + Garage FAR
                            </code>
                            <span className="block mt-3 text-[10px] text-blue-600 font-medium italic">
                                *Note: A represents the Net Site Area in thousands of square feet. This specific step-down logic is used exclusively to calculate the Floor Area Ratio (FAR) for Los Gatos R-1 zone properties.
                            </span>
                        </div>
                    )}

                    {/* Logic Protocol Flow - Step by Step */}
                    <div className="relative group transition-all">
                        <LogicFlow code={profile.formula} />
                    </div>

                    {/* Logic Context Summary */}
                    <div className="flex items-center gap-2 px-2 pt-2">
                        <div className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]" />
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.1em]">
                            {profile.logic}
                        </p>
                    </div>
                </div>
            </div>
        );
    };


    // Precise ordering: Slope, LUF, MFA, MDA
    const cityName = (city.name || "").toUpperCase();
    const isSaratoga = cityName.includes("SARATOGA");
    const isCupertino = cityName.includes("CUPERTINO");
    const isMonteSereno = cityName.includes("MONTE SERENO");
    const isLosGatos = cityName.includes("LOS GATOS");
    const isMcKinney = cityName.includes("MCKINNEY");
    const isFrisco = cityName.includes("FRISCO");

    const mfaTitle = isSaratoga ? "Floor Area Allowance" : (isCupertino ? "Maximum Allowable Floor Area" : (isMonteSereno ? "Maximum Size of Main Dwelling Unit" : (isLosGatos ? "Floor Area Ratio (FAR)" : (isMcKinney || isFrisco ? "Maximum Floor Area" : "Maximum Floor Area (MFA)"))));
    const mdaTitle = isSaratoga ? "Site Coverage" : (isCupertino ? "Lot Coverage" : (isMonteSereno ? "Maximum Structural Coverage" : (isMcKinney || isFrisco ? "Maximum Lot Coverage" : "Maximum Development Area (MDA)")));

    const orderedProfiles = [
        { id: 'slope', label: 'Topographic Analysis', keys: ['slope', 'SLOPE', 'slope_formula'] },
        { id: 'luf', label: 'Unit Factor Scaling', keys: ['luf', 'LUF', 'unit_factor', 'luf_formula'] },
        { id: 'mfa', label: mfaTitle, keys: ['mfa', 'MFA', 'mfa_formula', 'floor_area', 'max_floor_area', 'MFA_TABLE'] },
        { id: 'mda', label: mdaTitle, keys: ['mda', 'MDA', 'mda_formula', 'development_area', 'max_development_area', 'coverage'] }
    ].map(ref => {
        if (isLosGatos && ref.id === 'mda') return null;

        let profileData = null;
        if (city.profiles) {
            for (const key of ref.keys) {
                if (city.profiles[key]) {
                    profileData = { ...city.profiles[key], key };
                    break;
                }
            }
        }

        if (!profileData) return null;

        return {
            ...profileData,
            title: profileData.title || ref.label
        };
    }).filter(Boolean);

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 overflow-hidden font-sans no-cursive">
                    <style>{`
                        .no-cursive * { font-family: 'Inter', system-ui, -apple-system, sans-serif !important; }
                        .no-cursive .font-mono { font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace !important; }
                        .custom-scrollbar::-webkit-scrollbar { height: 4px; width: 4px; }
                        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                        .custom-scrollbar::-webkit-scrollbar-thumb { background: #334155; border-radius: 10px; }
                        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #475569; }
                        .no-scrollbar-y::-webkit-scrollbar { height: 4px; }
                    `}</style>

                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="absolute inset-0 bg-slate-950/10 backdrop-blur-sm print:hidden"
                    />

                    <motion.div
                        initial={{ opacity: 0, scale: 0.995, y: 10 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.995, y: 10 }}
                        className="relative w-full max-w-4xl h-[90vh] flex flex-col overflow-hidden bg-white shadow-2xl rounded-xl border border-slate-200/50"
                    >
                        {/* Header Toolbar - Minimal & Efficient */}
                        <div className="px-6 py-4 flex items-center justify-between z-10 border-b border-slate-100 print:hidden bg-white/80 backdrop-blur-md">
                            <div className="flex flex-col">
                                <div className="flex items-center gap-1.5 mb-0.5">
                                    <FileText className="w-3 h-3 text-indigo-600" />
                                    <h2 className="text-[8px] font-black text-indigo-600 uppercase tracking-[0.4em]">Node Registry // Technical Standard</h2>
                                </div>
                                <h1 className="text-xl font-black text-slate-900 tracking-tight leading-none">
                                    {city.name}
                                </h1>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={handleDownload}
                                    className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-[9px] font-black uppercase tracking-widest hover:bg-indigo-700 transition-all flex items-center gap-2 shadow-xl shadow-indigo-100"
                                >
                                    <Download className="w-3.5 h-3.5" />
                                    Export Analysis
                                </button>
                                <button
                                    onClick={onClose}
                                    className="w-10 h-10 bg-slate-50 text-slate-400 border border-slate-100 rounded-lg hover:bg-slate-900 hover:text-white transition-all flex items-center justify-center group"
                                >
                                    <X className="w-4 h-4 group-hover:rotate-90 transition-transform" />
                                </button>
                            </div>
                        </div>

                        {/* Content View - Dense & Technical */}
                        <div className="flex-1 overflow-y-auto p-6 lg:p-12 no-scrollbar bg-white">
                            <div className="max-w-2xl mx-auto">

                                {/* Print Optimized Header */}
                                <div className="hidden print:block mb-8 pb-4 border-b-2 border-slate-900">
                                    <h1 className="text-2xl font-black text-slate-900 tracking-tight">Technical Analysis: {city.name}</h1>
                                    <p className="text-[8px] font-black text-slate-400 uppercase tracking-[0.3em]">Verified Municipal Standard Registry v7.2</p>
                                </div>

                                {orderedProfiles.map((profile, idx) => (
                                    <div key={profile.key}>
                                        <SectionHeader number={idx + 1} title={profile.title} />
                                        <FormulaBlock profile={profile} />
                                    </div>
                                ))}

                                {/* Binary Standard Footer */}
                                <div className="mt-8 pt-8 border-t border-slate-100 space-y-6 pb-8">
                                    <div className="flex flex-col gap-2">
                                        <div className="p-4 bg-amber-50/50 border border-amber-100 rounded-xl">
                                            <span className="text-[7px] font-black text-amber-600 uppercase tracking-[0.4em] block mb-1">Administrative Disclaimer</span>
                                            <p className="text-[10px] font-bold text-amber-800 leading-relaxed italic-none">
                                                {city.disclaimer}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between gap-6">
                                        <div className="flex flex-col">
                                            <span className="text-[7px] font-black text-slate-300 uppercase tracking-[0.5em] mb-1.5">Verification Core</span>
                                            <p className="text-[10px] font-black text-slate-400 leading-tight">
                                                Logic node for {city.name} is synchronized with jurisdictional code.
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-4">
                                            <div className="flex flex-col items-end">
                                                <span className="text-[8px] font-black text-indigo-500 tracking-widest">Core v7.2</span>
                                                <span className="text-[9px] font-black text-slate-900">SYS_OPTIMIZED</span>
                                            </div>
                                            <div className="w-8 h-8 border-2 border-slate-50 rounded-md flex items-center justify-center bg-slate-50/30">
                                                <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default FormulaReportModal;
